// src/components/legal/DpaPage.js — public /dpa page (C1, C3).
import { Link } from "react-router-dom";
import { LegalDocShell } from "./LegalDocShell";

export const DpaPage = () => (
  <LegalDocShell
    docKey="dpa"
    title="Data Processing Agreement"
    description="Sahod Data Processing Agreement — processor terms for workforce data under the Philippine Data Privacy Act (RA 10173)."
  >
    <div className="legal-doc__lede">
      This Data Processing Agreement (&ldquo;DPA&rdquo;) forms part of the{" "}
      <Link to="/terms">Terms of Service</Link> between the Customer and Sahod.Solutions
      (&ldquo;Sahod&rdquo;) and applies wherever Sahod processes personal data on the
      Customer&rsquo;s behalf. It is written primarily under the Philippine{" "}
      <strong>Data Privacy Act of 2012 (RA 10173)</strong> and its Implementing Rules, which
      govern the workforce data processed through the Service. Where this DPA and the Terms
      conflict on data protection, this DPA prevails.
    </div>

    <h2>1. Roles</h2>
    <p>
      For Employee Data, the <strong>Customer is the Personal Information Controller (PIC)</strong>{" "}
      and <strong>Sahod is the Personal Information Processor (PIP)</strong>. Sahod processes
      Employee Data only to provide the Service and only on the Customer&rsquo;s documented
      instructions, which include these Terms and the Customer&rsquo;s configuration and use of
      the Service. Sahod will tell the Customer if, in its opinion, an instruction breaches
      applicable data protection law.
    </p>

    <h2>2. Sahod&rsquo;s obligations</h2>
    <ul>
      <li>Process Employee Data only as set out in Section 1 and Annex A, and not for its own purposes;</li>
      <li>Ensure personnel authorised to process Employee Data are bound by confidentiality;</li>
      <li>Implement and maintain the security measures in Annex B;</li>
      <li>
        Assist the Customer, taking into account the nature of processing, in responding to
        data-subject requests and in meeting its breach-notification, security, and
        privacy-impact-assessment obligations;
      </li>
      <li>Make available information reasonably necessary to demonstrate compliance with this DPA.</li>
    </ul>

    <h2>3. Sub-processors</h2>
    <p>
      The Customer provides general authorisation for Sahod to engage the sub-processors listed in
      Annex C to deliver the Service. Sahod imposes data protection obligations on each
      sub-processor that are no less protective than this DPA, and remains responsible for their
      performance. Sahod will give the Customer reasonable prior notice of any new or replacement
      sub-processor that processes Employee Data; if the Customer reasonably objects on data
      protection grounds, the parties will work in good faith to resolve it, and the Customer may
      terminate the affected Service if it cannot be resolved.
    </p>

    <h2>4. Cross-border processing</h2>
    <p>
      Employee Data is stored and processed in <strong>Singapore</strong> and may be accessed by
      Sahod&rsquo;s operating entity in{" "}
      <strong>Australia</strong> for the purpose of providing and supporting the Service. Account
      authentication identifiers (email addresses and user IDs) are processed by our authentication
      and identity provider in the <strong>United States</strong>, as that provider does not
      currently offer regional storage; the payroll and sensitive personal data described in Annex
      A remains in Singapore. Sahod takes reasonable steps to ensure that Employee Data transferred outside
      the Philippines receives a standard of protection comparable to that required under RA
      10173, through contractual and technical safeguards. The Customer, as PIC, remains
      accountable for personal data it transfers to Sahod and authorises these transfers by using
      the Service.
    </p>

    <h2>5. Data-subject rights</h2>
    <p>
      The Customer is responsible for responding to its employees&rsquo; requests to access,
      correct, object to, restrict, port, or erase their data. Sahod will provide reasonable
      assistance and the tools within the Service to enable the Customer to do so. Where Sahod
      receives such a request directly, it will refer the data subject to the Customer.
    </p>

    <h2>6. Personal data breach</h2>
    <p>
      Sahod will notify the Customer without undue delay after becoming aware of a personal data
      breach affecting Employee Data, and will provide information reasonably available to help the
      Customer meet its obligations &mdash; including notifying the National Privacy Commission and
      affected data subjects within <strong>72 hours</strong> where required. Sahod will take
      reasonable steps to mitigate and remediate the breach. A notification is not an admission of
      fault.
    </p>

    <h2>7. Audit</h2>
    <p>
      On reasonable prior written notice, and no more than once in any 12-month period (unless
      required by a regulator or following a breach), Sahod will make available information
      necessary to demonstrate compliance with this DPA, including relevant certifications and
      summaries of controls. Any on-site audit will be at the Customer&rsquo;s cost, during
      business hours, subject to confidentiality, and conducted so as not to disrupt the Service or
      compromise other customers&rsquo; data.
    </p>

    <h2>8. Return and deletion</h2>
    <p>
      On termination, the Customer may export its Employee Data within a reasonable window. After
      that window, Sahod will delete or anonymise Employee Data in its active systems, with backups
      purged on their normal cycle, except where retention is required by law. On request, Sahod
      will confirm deletion in writing.
    </p>

    <h2>9. Liability</h2>
    <p>
      Each party&rsquo;s liability under this DPA is subject to the limitations of liability in the{" "}
      <Link to="/terms">Terms of Service</Link>. This DPA does not increase Sahod&rsquo;s aggregate
      liability beyond the cap stated there.
    </p>

    <h2>10. Term and governing law</h2>
    <p>
      This DPA takes effect when the Customer accepts the Terms and continues for as long as Sahod
      processes Employee Data. It is governed by the law stated in the Terms, without prejudice to
      the application of RA 10173 to the processing of personal data of individuals in the
      Philippines.
    </p>

    <div className="legal-doc__annex">
      <h2>Annex A &mdash; Details of processing</h2>
      <table className="legal-doc__table">
        <tbody>
          <tr>
            <th>Subject matter</th>
            <td>Provision of the Sahod payroll and workforce-management Service</td>
          </tr>
          <tr>
            <th>Duration</th>
            <td>For the term of the subscription, plus any export and deletion window</td>
          </tr>
          <tr>
            <th>Nature &amp; purpose</th>
            <td>
              Storage, computation, and generation of payroll, payslips, bank files, and statutory
              records on the Customer&rsquo;s instructions
            </td>
          </tr>
          <tr>
            <th>Categories of data subjects</th>
            <td>The Customer&rsquo;s employees and other workforce members</td>
          </tr>
          <tr>
            <th>Categories of personal data</th>
            <td>
              Identity and contact details; date of birth, sex, marital status; employment and
              compensation data; time, attendance, and leave records
            </td>
          </tr>
          <tr>
            <th>Sensitive personal information</th>
            <td>
              Government identifiers (SSS, TIN, PhilHealth, Pag-IBIG) and bank account details
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="legal-doc__annex">
      <h2>Annex B &mdash; Security measures</h2>
      <ul>
        <li>Encryption of data in transit and at rest;</li>
        <li>Multi-tenant isolation so one Customer&rsquo;s data cannot be accessed by another;</li>
        <li>Role-based access control and least-privilege permissions;</li>
        <li>Application-layer abuse and bot protection, and database-layer access rules;</li>
        <li>Audit logging of payroll and account events;</li>
        <li>Backup and recovery procedures.</li>
      </ul>
    </div>

    <div className="legal-doc__annex">
      <h2>Annex C &mdash; Authorised sub-processors</h2>
      <table className="legal-doc__table">
        <tbody>
          <tr>
            <th>Function</th>
            <th>Purpose</th>
            <th>Location</th>
          </tr>
          <tr>
            <td>Cloud infrastructure</td>
            <td>Hosting, database, storage, compute</td>
            <td>Singapore</td>
          </tr>
          <tr>
            <td>Authentication and identity</td>
            <td>Account sign-in and identity (email, user IDs)</td>
            <td>United States</td>
          </tr>
          <tr>
            <td>Payment processing</td>
            <td>Subscription billing and payments (PCI-DSS)</td>
            <td>Provider&rsquo;s regions</td>
          </tr>
          <tr>
            <td>Email delivery</td>
            <td>Transactional and billing email</td>
            <td>Provider&rsquo;s regions</td>
          </tr>
        </tbody>
      </table>
      <div className="legal-doc__note">
        Our authentication and identity provider has no regional storage option and holds account
        identifiers (email, user IDs) in the United States. The sensitive payroll data in Annex A
        remains in Singapore.
      </div>
    </div>
  </LegalDocShell>
);
